import { Component, OnDestroy, OnInit } from '@angular/core'

import { Observable, Subject } from 'rxjs'
import { settingSelectors } from './core/settings/settings.selectors'
import { takeUntil } from 'rxjs/operators'
import { NavigationEnd, Router } from '@angular/router'
import { Store } from '@ngrx/store'
import { filter } from 'rxjs/operators'
import { BreadCrumbActions } from './shared/components/breadcrumb/breadcrumb.actions'
import {
  ResourceType,
  ROUTE_PATTERNS,
  WCAG_TITLE_TRANSLATIONS
} from './shared/constants'
import { toCamelCase } from './shared/until'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  public selectedLanguage$: Observable<string>
  private destroy$ = new Subject<void>()

  constructor(private router: Router, private store: Store) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects
        const parsedUrl = this.router.parseUrl(url)

        const dispatchHistory = (
          section: string,
          titleKeys: string[],
          path: string,
          resourceType: ResourceType,
          entityId?: number
        ) => {
          this.store.dispatch(
            BreadCrumbActions.initHistory({
              pageName: section,
              url: path,
              titleName: titleKeys.filter(Boolean),
              entityId,
              resourceType
            })
          )
        }

        // 1. Detect: /X/number/Y
        const matchThree = url.match(ROUTE_PATTERNS.withIdAndSubsection)
        if (matchThree) {
          const [_, section, id, subsection] = matchThree
          return dispatchHistory(
            toCamelCase(section),
            [
              WCAG_TITLE_TRANSLATIONS[toCamelCase(section)][
                toCamelCase(subsection)
              ]
            ],
            WCAG_TITLE_TRANSLATIONS[toCamelCase(section)]['path'],
            WCAG_TITLE_TRANSLATIONS[toCamelCase(section)]['resource'],
            Number(id)
          )
        }

        // 2. Detect: /X/number
        const matchTwo = url.match(ROUTE_PATTERNS.withId)
        if (matchTwo) {
          const [_, section, id] = matchTwo
          return dispatchHistory(
            toCamelCase(section),
            [WCAG_TITLE_TRANSLATIONS['common']['general']],
            WCAG_TITLE_TRANSLATIONS[toCamelCase(section)]['path'],
            WCAG_TITLE_TRANSLATIONS[toCamelCase(section)]['resource'],
            Number(id)
          )
        }

        // 3. Detect: /X/create
        const matchCreate = url.match(ROUTE_PATTERNS.withCreateSubsection)
        if (matchCreate) {
          const [_, section] = matchCreate

          return dispatchHistory(
            WCAG_TITLE_TRANSLATIONS[toCamelCase(section)]['main'],
            [
              WCAG_TITLE_TRANSLATIONS[toCamelCase(section)]['create'],
              WCAG_TITLE_TRANSLATIONS['common']['create']
            ],
            WCAG_TITLE_TRANSLATIONS[toCamelCase(section)]['path'],
            WCAG_TITLE_TRANSLATIONS[toCamelCase(section)]['resource']
          )
        }

        // 4. Detect: (modal:number/modal-prod) or (modal:number/modal-prod/details)
        const baseRoute = parsedUrl.root.children['primary']?.segments[0]?.path
        const modalGroup = parsedUrl.root.children['modal']

        if (baseRoute && modalGroup) {
          const segments = modalGroup.segments
          if (segments.length >= 2) {
            const entityId = segments[0]?.path
            const modalParts = segments.slice(1)
            const capitalize = (str?: string) =>
              str ? str.charAt(0).toUpperCase() + str.slice(1) : ''
            let subsection = ''
            if (modalParts[1]?.path && modalParts[2]?.path) {
              subsection = modalParts[1].path + capitalize(modalParts[2].path)
            } else if (modalParts[1]?.path) {
              subsection = modalParts[1].path
            }
            if (!subsection) {
              return dispatchHistory(
                toCamelCase(baseRoute),
                baseRoute === 'courses'
                  ? [
                      WCAG_TITLE_TRANSLATIONS[toCamelCase(baseRoute)][
                        'participation'
                      ]
                    ]
                  : [WCAG_TITLE_TRANSLATIONS['common']['general']],
                WCAG_TITLE_TRANSLATIONS[toCamelCase(baseRoute)]['path'],
                WCAG_TITLE_TRANSLATIONS[toCamelCase(baseRoute)]['resource'],
                Number(entityId)
              )
            } else {
              return dispatchHistory(
                toCamelCase(baseRoute),
                [
                  WCAG_TITLE_TRANSLATIONS[toCamelCase(baseRoute)][
                    toCamelCase(subsection)
                  ]
                ],
                WCAG_TITLE_TRANSLATIONS[toCamelCase(baseRoute)]['path'],
                WCAG_TITLE_TRANSLATIONS[toCamelCase(baseRoute)]['resource'],
                Number(entityId)
              )
            }
          }
        }
        // 5.  Detect: /X only
        const matchFive = url.match(ROUTE_PATTERNS.singleSection)
        if (matchFive) {
          const [_, section] = matchFive
          if (section.includes('dashboard')) {
            return
          }
          if (
            WCAG_TITLE_TRANSLATIONS.hasOwnProperty.call(toCamelCase(section))
          ) {
            return dispatchHistory(
              toCamelCase(baseRoute),
              [WCAG_TITLE_TRANSLATIONS[toCamelCase(section)]['main']],
              WCAG_TITLE_TRANSLATIONS[toCamelCase(section)]['path'],
              WCAG_TITLE_TRANSLATIONS[toCamelCase(baseRoute)]['resource']
            )
          }
          return
        }
      })
  }

  ngOnInit() {
    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )

    this.selectedLanguage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((language) => {
        document.documentElement.lang = language || 'en'
      })
  }
  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }
}
